import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  Calculator, 
  PiggyBank, 
  CreditCard, 
  Plus,
  IndianRupee,
  Loader2,
  Trash2
} from 'lucide-react';
import { GlassCard } from '../components/layout/GlassCard';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const expenseCategories = [
  'Food', 'Transport', 'Shopping', 'Entertainment', 'Bills', 'Health', 'Education', 'Other'
];

export default function InvestmentGuruPage() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('expenses');
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  // Expense form
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseCategory, setExpenseCategory] = useState('Food');
  const [expenseDesc, setExpenseDesc] = useState('');

  // Calculator states
  const [sipAmount, setSipAmount] = useState('10000');
  const [sipRate, setSipRate] = useState('12');
  const [sipYears, setSipYears] = useState('10');
  const [sipResult, setSipResult] = useState(null);

  const [emiPrincipal, setEmiPrincipal] = useState('1000000');
  const [emiRate, setEmiRate] = useState('10');
  const [emiMonths, setEmiMonths] = useState('60');
  const [emiResult, setEmiResult] = useState(null);

  const [fdPrincipal, setFdPrincipal] = useState('100000');
  const [fdRate, setFdRate] = useState('7');
  const [fdYears, setFdYears] = useState('3');
  const [fdResult, setFdResult] = useState(null);

  const loadExpenses = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/expenses`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setExpenses(response.data);
    } catch (error) {
      console.error('Failed to load expenses');
    }
  }, [token]);

  const loadSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/expenses/summary`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Failed to load summary');
    }
  }, [token]);

  useEffect(() => {
    loadExpenses();
    loadSummary();
  }, [loadExpenses, loadSummary]);

  const addExpense = async (e) => {
    e.preventDefault();
    if (!expenseAmount) {
      toast.error('Please enter an amount');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/expenses`,
        {
          amount: parseFloat(expenseAmount),
          category: expenseCategory,
          description: expenseDesc
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      toast.success('Expense added');
      setExpenseAmount('');
      setExpenseDesc('');
      loadExpenses();
      loadSummary();
    } catch (error) {
      toast.error('Failed to add expense');
    }
  };

  const deleteExpense = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/expenses/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success('Expense deleted');
      loadExpenses();
      loadSummary();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const calculateSIP = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/calculator/sip`,
        {
          monthly_investment: parseFloat(sipAmount),
          annual_rate: parseFloat(sipRate),
          years: parseInt(sipYears)
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      setSipResult(response.data);
    } catch (error) {
      toast.error('Calculation failed');
    } finally {
      setLoading(false);
    }
  };

  const calculateEMI = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/calculator/emi`,
        {
          principal: parseFloat(emiPrincipal),
          annual_rate: parseFloat(emiRate),
          tenure_months: parseInt(emiMonths)
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      setEmiResult(response.data);
    } catch (error) {
      toast.error('Calculation failed');
    } finally {
      setLoading(false);
    }
  };

  const calculateFD = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/calculator/fd`,
        {
          principal: parseFloat(fdPrincipal),
          annual_rate: parseFloat(fdRate),
          years: parseInt(fdYears)
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      setFdResult(response.data);
    } catch (error) {
      toast.error('Calculation failed');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'expenses', label: 'Expense Tracker', icon: CreditCard },
    { id: 'sip', label: 'SIP Calculator', icon: TrendingUp },
    { id: 'emi', label: 'EMI Calculator', icon: Calculator },
    { id: 'fd', label: 'FD Calculator', icon: PiggyBank },
  ];

  return (
    <div className="max-w-6xl mx-auto" data-testid="investment-guru-page">
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl font-bold mb-2">Investment Guru</h1>
        <p className="text-gray-400">Your personal financial advisor</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-8">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full transition-all ${
              activeTab === tab.id
                ? 'bg-primary text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
            data-testid={`${tab.id}-tab`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Expense Tracker */}
      {activeTab === 'expenses' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Summary Card */}
          <GlassCard className="p-6">
            <h3 className="font-heading text-lg font-semibold mb-4">This Month</h3>
            {summary && (
              <>
                <div className="text-3xl font-bold text-accent mb-4">
                  ₹{summary.total.toLocaleString()}
                </div>
                <div className="space-y-2">
                  {Object.entries(summary.by_category).map(([cat, amount]) => (
                    <div key={cat} className="flex justify-between text-sm">
                      <span className="text-gray-400">{cat}</span>
                      <span>₹{amount.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </GlassCard>

          {/* Add Expense */}
          <GlassCard className="p-6">
            <h3 className="font-heading text-lg font-semibold mb-4">Add Expense</h3>
            <form onSubmit={addExpense} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Amount</label>
                <div className="relative">
                  <IndianRupee className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  <input
                    type="number"
                    value={expenseAmount}
                    onChange={(e) => setExpenseAmount(e.target.value)}
                    className="input-field w-full pl-12"
                    placeholder="0"
                    data-testid="expense-amount-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Category</label>
                <select
                  value={expenseCategory}
                  onChange={(e) => setExpenseCategory(e.target.value)}
                  className="input-field w-full"
                  data-testid="expense-category-select"
                >
                  {expenseCategories.map((cat) => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Description</label>
                <input
                  type="text"
                  value={expenseDesc}
                  onChange={(e) => setExpenseDesc(e.target.value)}
                  className="input-field w-full"
                  placeholder="Optional"
                  data-testid="expense-desc-input"
                />
              </div>
              <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" data-testid="add-expense-btn">
                <Plus className="w-4 h-4" />
                Add Expense
              </button>
            </form>
          </GlassCard>

          {/* Recent Expenses */}
          <GlassCard className="p-6">
            <h3 className="font-heading text-lg font-semibold mb-4">Recent Expenses</h3>
            <div className="space-y-2 max-h-80 overflow-y-auto custom-scrollbar">
              {expenses.slice(0, 10).map((exp) => (
                <div key={exp.expense_id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                  <div>
                    <p className="font-medium">₹{exp.amount.toLocaleString()}</p>
                    <p className="text-xs text-gray-400">{exp.category}{exp.description ? ` - ${exp.description}` : ''}</p>
                  </div>
                  <button
                    onClick={() => deleteExpense(exp.expense_id)}
                    className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>
      )}

      {/* SIP Calculator */}
      {activeTab === 'sip' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <GlassCard className="p-6">
            <h3 className="font-heading text-xl font-semibold mb-6">SIP Calculator</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Monthly Investment (₹)</label>
                <input
                  type="number"
                  value={sipAmount}
                  onChange={(e) => setSipAmount(e.target.value)}
                  className="input-field w-full"
                  data-testid="sip-amount-input"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Expected Return Rate (%)</label>
                <input
                  type="number"
                  value={sipRate}
                  onChange={(e) => setSipRate(e.target.value)}
                  className="input-field w-full"
                  data-testid="sip-rate-input"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Investment Period (Years)</label>
                <input
                  type="number"
                  value={sipYears}
                  onChange={(e) => setSipYears(e.target.value)}
                  className="input-field w-full"
                  data-testid="sip-years-input"
                />
              </div>
              <button
                onClick={calculateSIP}
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="calculate-sip-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Calculate'}
              </button>
            </div>
          </GlassCard>

          {sipResult && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <GlassCard className="p-6 h-full">
                <h3 className="font-heading text-xl font-semibold mb-6">SIP Results</h3>
                <div className="space-y-4">
                  <div className="p-4 bg-white/5 rounded-xl">
                    <p className="text-sm text-gray-400">Invested Amount</p>
                    <p className="text-2xl font-bold">₹{sipResult.invested_amount.toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-primary/10 rounded-xl">
                    <p className="text-sm text-gray-400">Estimated Returns</p>
                    <p className="text-2xl font-bold text-primary">₹{sipResult.estimated_returns.toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-accent/10 rounded-xl">
                    <p className="text-sm text-gray-400">Total Value</p>
                    <p className="text-3xl font-bold text-accent">₹{sipResult.total_value.toLocaleString()}</p>
                  </div>
                </div>
              </GlassCard>
            </motion.div>
          )}
        </div>
      )}

      {/* EMI Calculator */}
      {activeTab === 'emi' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <GlassCard className="p-6">
            <h3 className="font-heading text-xl font-semibold mb-6">EMI Calculator</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Loan Amount (₹)</label>
                <input
                  type="number"
                  value={emiPrincipal}
                  onChange={(e) => setEmiPrincipal(e.target.value)}
                  className="input-field w-full"
                  data-testid="emi-principal-input"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Interest Rate (%)</label>
                <input
                  type="number"
                  value={emiRate}
                  onChange={(e) => setEmiRate(e.target.value)}
                  className="input-field w-full"
                  data-testid="emi-rate-input"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Tenure (Months)</label>
                <input
                  type="number"
                  value={emiMonths}
                  onChange={(e) => setEmiMonths(e.target.value)}
                  className="input-field w-full"
                  data-testid="emi-months-input"
                />
              </div>
              <button
                onClick={calculateEMI}
                disabled={loading}
                className="btn-primary w-full"
                data-testid="calculate-emi-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Calculate'}
              </button>
            </div>
          </GlassCard>

          {emiResult && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <GlassCard className="p-6 h-full">
                <h3 className="font-heading text-xl font-semibold mb-6">EMI Results</h3>
                <div className="space-y-4">
                  <div className="p-4 bg-accent/10 rounded-xl">
                    <p className="text-sm text-gray-400">Monthly EMI</p>
                    <p className="text-3xl font-bold text-accent">₹{emiResult.emi.toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-xl">
                    <p className="text-sm text-gray-400">Total Interest</p>
                    <p className="text-2xl font-bold">₹{emiResult.total_interest.toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-xl">
                    <p className="text-sm text-gray-400">Total Payment</p>
                    <p className="text-2xl font-bold">₹{emiResult.total_payment.toLocaleString()}</p>
                  </div>
                </div>
              </GlassCard>
            </motion.div>
          )}
        </div>
      )}

      {/* FD Calculator */}
      {activeTab === 'fd' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <GlassCard className="p-6">
            <h3 className="font-heading text-xl font-semibold mb-6">FD Calculator</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Principal Amount (₹)</label>
                <input
                  type="number"
                  value={fdPrincipal}
                  onChange={(e) => setFdPrincipal(e.target.value)}
                  className="input-field w-full"
                  data-testid="fd-principal-input"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Interest Rate (%)</label>
                <input
                  type="number"
                  value={fdRate}
                  onChange={(e) => setFdRate(e.target.value)}
                  className="input-field w-full"
                  data-testid="fd-rate-input"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-2">Period (Years)</label>
                <input
                  type="number"
                  value={fdYears}
                  onChange={(e) => setFdYears(e.target.value)}
                  className="input-field w-full"
                  data-testid="fd-years-input"
                />
              </div>
              <button
                onClick={calculateFD}
                disabled={loading}
                className="btn-primary w-full"
                data-testid="calculate-fd-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Calculate'}
              </button>
            </div>
          </GlassCard>

          {fdResult && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
              <GlassCard className="p-6 h-full">
                <h3 className="font-heading text-xl font-semibold mb-6">FD Results</h3>
                <div className="space-y-4">
                  <div className="p-4 bg-white/5 rounded-xl">
                    <p className="text-sm text-gray-400">Principal</p>
                    <p className="text-2xl font-bold">₹{fdResult.principal.toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-primary/10 rounded-xl">
                    <p className="text-sm text-gray-400">Interest Earned</p>
                    <p className="text-2xl font-bold text-primary">₹{fdResult.interest_earned.toLocaleString()}</p>
                  </div>
                  <div className="p-4 bg-accent/10 rounded-xl">
                    <p className="text-sm text-gray-400">Maturity Amount</p>
                    <p className="text-3xl font-bold text-accent">₹{fdResult.maturity_amount.toLocaleString()}</p>
                  </div>
                </div>
              </GlassCard>
            </motion.div>
          )}
        </div>
      )}
    </div>
  );
}
